<script setup lang="ts">
// First layer of the cookie banner: a fixed bar at the bottom of the viewport.
//
// NON-BLOCKING by design - no backdrop, no focus trap, no scroll lock. The page
// behind stays fully usable, so declining to answer is possible and there is no
// "cookie wall". It is rendered first in the DOM (see CookieConsent.vue) so
// keyboard and screen-reader users reach it without it having to steal focus.
//
// Accept / reject / customise share one class constant, so they have identical
// visual weight (see consentStyles.ts).
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { toCookieLocale } from '@/content/cookies/cookieInventory'
import { useConsentStore } from '@/stores/consentStore'
import { BANNER_HEIGHT_VAR, CONSENT_ACTION_BAR_CLASS, CONSENT_ACTION_CLASS } from './consentStyles'

const { t, locale } = useI18n()
const store = useConsentStore()

const lang = computed(() => toCookieLocale(String(locale.value)))
const cookiePolicyHref = computed(() =>
  lang.value === 'es' ? '/cookies' : `/${lang.value}/cookies`,
)
const privacyHref = computed(() => `/${lang.value}/legal`)

// Publishes the bar's height so the page can scroll its content clear of it (see consentStyles.ts).
// Once up front, so there is no frame where the bar covers the page unpadded, then on every resize.
const bar = ref<HTMLElement | null>(null)
let observer: ResizeObserver | undefined
onMounted(() => {
  const el = bar.value
  if (!el) return
  const publish = () =>
    document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`)
  publish()
  if (typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(publish)
  observer.observe(el)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR)
})
</script>

<template>
  <section
    ref="bar"
    class="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex justify-center p-3 sm:p-4"
    aria-labelledby="tpa-cookie-banner-title"
    aria-describedby="tpa-cookie-banner-text"
    data-testid="cookie-banner"
  >
    <div
      class="pointer-events-auto w-full max-w-3xl overflow-hidden rounded-[15px] border border-[var(--color-cool-200)] bg-white shadow-[0_8px_30px_rgba(22,35,59,0.25)]"
    >
      <div class="px-5 pb-4 pt-5 sm:px-6">
        <h2 id="tpa-cookie-banner-title" class="text-lg font-bold! text-smartway-blue">
          {{ t('components.cookieConsent.banner.title') }}
        </h2>
        <p id="tpa-cookie-banner-text" class="mt-2 text-sm leading-relaxed text-[var(--color-ink)]">
          {{ t('components.cookieConsent.banner.text') }}
        </p>
        <nav
          class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm"
          :aria-label="t('components.cookieConsent.banner.linksLabel')"
        >
          <a
            :href="cookiePolicyHref"
            target="_blank"
            rel="noopener noreferrer"
            class="font-semibold! text-smartway-blue underline underline-offset-2"
          >
            {{ t('components.cookieConsent.banner.cookiePolicy') }}
            <span class="sr-only">{{ t('components.cookieConsent.banner.opensInNewTab') }}</span>
          </a>
          <a
            :href="privacyHref"
            target="_blank"
            rel="noopener noreferrer"
            class="font-semibold! text-smartway-blue underline underline-offset-2"
          >
            {{ t('components.cookieConsent.banner.privacyPolicy') }}
            <span class="sr-only">{{ t('components.cookieConsent.banner.opensInNewTab') }}</span>
          </a>
        </nav>
      </div>

      <div :class="CONSENT_ACTION_BAR_CLASS">
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-cookie-return-focus
          data-testid="cookie-customize"
          @click="store.openPreferences()"
        >
          {{ t('components.cookieConsent.banner.customize') }}
        </button>
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-testid="cookie-reject-all"
          @click="store.rejectAll()"
        >
          {{ t('components.cookieConsent.banner.rejectAll') }}
        </button>
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-testid="cookie-accept-all"
          @click="store.acceptAll()"
        >
          {{ t('components.cookieConsent.banner.acceptAll') }}
        </button>
      </div>
    </div>
  </section>
</template>
